import type { ApplicationCredentials } from '@broker/contracts';
import { useState } from 'react';
import { useApplications, useCreateApplication, useRotateSecret, useUpdateApplication } from '../api/queries.js';
import { Badge, Button, Card, Field, Loading, Mono, Page, Problem, Table } from '../components/ui.jsx';
import { formatNumber, formatRelative } from '../utils/format.js';

/**
 * Applications are the only identities that can talk to Broker. Registering one hands back a
 * client secret exactly once — there is no route that can read it again, so the console shows it
 * until the person acknowledges it and then forgets it too.
 */
export function Applications() {
  const applications = useApplications();
  const create = useCreateApplication();
  const update = useUpdateApplication();
  const rotate = useRotateSecret();

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [credentials, setCredentials] = useState<(ApplicationCredentials & { slug: string }) | undefined>();

  if (applications.isLoading) return <Loading what="le applicazioni" />;
  if (applications.error) return <Problem error={applications.error} />;

  const register = async () => {
    const result = await create.mutateAsync({ slug, name });
    setCredentials({ ...result.credentials, slug: result.application.slug });
    setSlug('');
    setName('');
  };

  return (
    <Page eyebrow="CONFIGURA" title="Applications" description="Chi può parlare con Broker, e con quali credenziali.">
      {credentials ? (
        <Card className="callout" title={`Credenziali di ${credentials.slug}`} hint="Visibili una sola volta">
          <p>
            Copiale adesso: di questo segreto Broker conserva solo l&apos;impronta SHA-256. Se le perdi, si rigenerano — le
            vecchie smettono di funzionare all&apos;istante.
          </p>
          <dl className="credentials">
            <dt>Client ID</dt>
            <dd>
              <Mono value={credentials.clientId} />
            </dd>
            <dt>Client secret</dt>
            <dd>
              <Mono value={credentials.clientSecret} />
            </dd>
          </dl>
          <Button variant="primary" onClick={() => setCredentials(undefined)}>
            Le ho salvate
          </Button>
        </Card>
      ) : null}

      <Card title="Registra un&apos;applicazione">
        <form
          className="form-row"
          onSubmit={(event) => {
            event.preventDefault();
            void register();
          }}
        >
          <Field label="Slug" hint="minuscolo, es. crm oppure web-site">
            <input value={slug} onChange={(event) => setSlug(event.target.value)} placeholder="crm" required />
          </Field>
          <Field label="Nome">
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="CRM" required />
          </Field>
          <Button type="submit" variant="primary" disabled={create.isPending || !slug || !name}>
            {create.isPending ? 'Registro…' : 'Registra'}
          </Button>
        </form>
        {create.error ? <Problem error={create.error} /> : null}
      </Card>

      <Card title={`Applicazioni (${applications.data!.applications.length})`}>
        <Table
          head={
            <tr>
              <th>Applicazione</th>
              <th>Client ID</th>
              <th>Stato</th>
              <th className="right">Pubblicati</th>
              <th className="right">Consumati</th>
              <th>Ultima attività</th>
              <th />
            </tr>
          }
          empty="Nessuna applicazione registrata."
        >
          {applications.data!.applications.map((application) => (
            <tr key={application.id}>
              <td>
                <strong>{application.name}</strong>
                <br />
                <span className="muted small">{application.slug}</span>
              </td>
              <td>
                <Mono value={application.clientId} />
              </td>
              <td>
                {application.enabled ? (
                  <Badge tone={application.connected ? 'good' : 'neutral'}>{application.connected ? 'connessa' : 'attiva'}</Badge>
                ) : (
                  <Badge tone="bad">disattivata</Badge>
                )}
              </td>
              <td className="right">{formatNumber(application.publishedCount)}</td>
              <td className="right">{formatNumber(application.consumedCount)}</td>
              <td className="muted small">{formatRelative(application.lastSeenAt)}</td>
              <td className="row-actions">
                <Button
                  variant="ghost"
                  onClick={() => {
                    void update.mutateAsync({ slug: application.slug, enabled: !application.enabled });
                  }}
                >
                  {application.enabled ? 'Disattiva' : 'Attiva'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    void rotate.mutateAsync(application.slug).then((result) => setCredentials({ ...result.credentials, slug: application.slug }));
                  }}
                >
                  Ruota segreto
                </Button>
              </td>
            </tr>
          ))}
        </Table>
      </Card>
    </Page>
  );
}
